import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Botao, Chapa, Selo, type Estagio } from '../componentes/forma.tsx';
import { Lista } from '../componentes/painel.tsx';
import { api, ErroApi, query } from '../lib/api.ts';
import { dataBr, diasAtras, idade, TIPOS_EXAME, ultimoDiaDoMes } from '../lib/formato.ts';
import { useUsuario } from '../lib/sessao.tsx';
import type { ExameLinha } from './Fila.tsx';

/* A tela do médico não é a fila da recepção com menos colunas. O que ele
   precisa saber é: quem, que exame, há quanto tempo espera. Empresa, convênio e
   valores não entram na decisão de laudar e só ocupariam a linha. */

export function MinhaFila({
  assinados = false,
  solicitados = false,
}: {
  assinados?: boolean;
  /** Tela do médico que pede exames mas não lauda. O recorte de quem ele
   *  enxerga é feito no servidor; aqui muda só o que a tela oferece. */
  solicitados?: boolean;
}) {
  const eu = useUsuario();
  const navegar = useNavigate();
  const [de, setDe] = useState(() => diasAtras(assinados ? 30 : 120));

  const consulta = query({
    situacao: assinados || solicitados ? 'TODAS' : 'AGUARDANDO_LAUDO',
    de,
    ate: ultimoDiaDoMes(),
    ordem: 'DATA',
    porPagina: 100,
  });

  const fila = useQuery({
    queryKey: ['exames', 'medico', consulta],
    queryFn: ({ signal }) =>
      api.get<{ itens: ExameLinha[]; total: number }>(`/exames${consulta}`, signal),
    placeholderData: (anterior) => anterior,
    refetchInterval: 60_000,
  });

  const itens = (fila.data?.itens ?? []).filter((e) =>
    solicitados ? true : assinados ? e.laudoAssinadoEm : e.imagens > 0 && !e.laudoAssinadoEm,
  );

  const esperaEmDias = (iso: string) =>
    Math.round((Date.now() - new Date(`${iso}T12:00:00`).getTime()) / 86_400_000);

  return (
    <Layout>
      <Cabecalho
        titulo={
          solicitados ? 'Exames que acompanho' : assinados ? 'Laudos assinados' : 'Exames para laudar'
        }
        contagem={fila.isPending ? '' : `${itens.length}`}
      >
        <label className="fila__periodo">
          <span className="rotulo">Desde</span>
          <input
            className="entrada"
            type="date"
            value={de}
            onChange={(e) => setDe(e.target.value)}
            aria-label="Data inicial"
          />
        </label>
      </Cabecalho>

      {!eu.temAssinatura && !assinados && (
        <div className="cartao" style={{ padding: 'var(--e-3)', marginBottom: 'var(--e-3)' }}>
          <p className="aviso aviso--erro">
            Você ainda não tem assinatura cadastrada e por isso não consegue assinar laudos.
            Peça à clínica para cadastrar o PNG da sua assinatura.
          </p>
        </div>
      )}

      <Lista
        carregando={fila.isPending}
        recarregando={fila.isFetching}
        erro={fila.error instanceof ErroApi ? fila.error.message : undefined}
        vazio={
          itens.length === 0
            ? assinados
              ? { titulo: 'Nenhum laudo assinado no período', apoio: 'Amplie a data inicial.' }
              : { titulo: 'Nada esperando laudo', apoio: 'Quando uma radiografia chegar, ela aparece aqui.' }
            : undefined
        }
      >
        <table className="tabela tabela--fila">
          <thead>
            <tr>
              <th>Código</th>
              <th className="tabela--num">Data</th>
              <th>Paciente</th>
              <th>Exame</th>
              <th>Tipo</th>
              <th className="tabela--num">{assinados ? 'Assinado' : 'Esperando'}</th>
              <th className="so-leitor">Ação</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((e) => {
              const dias = esperaEmDias(e.dataExame);
              return (
                <tr
                  key={e.id}
                  tabIndex={0}
                  role="button"
                  onClick={() => navegar(`/laudos/${e.id}`)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      navegar(`/laudos/${e.id}`);
                    }
                  }}
                >
                  <td>
                    <Chapa codigo={e.codigo} estagio={e.situacao as Estagio} />
                  </td>
                  <td className="tabela--num mono">{dataBr(e.dataExame)}</td>
                  <td className="tabela__nome">
                    {e.pacienteNome}
                    <span className="tabela__apoio"> · {idade(e.pacienteNascimento)} anos</span>
                  </td>
                  <td className="fila__exame">{e.procedimentoNome}</td>
                  <td>{TIPOS_EXAME[e.tipo] ?? e.tipo}</td>
                  <td className="tabela--num">
                    {assinados ? (
                      dataBr((e.laudoAssinadoEm ?? '').slice(0, 10))
                    ) : (
                      /* Dias de espera em vez da data: é o que decide a ordem
                         de atendimento, e ler "há 12 dias" é mais rápido que
                         calcular a diferença mentalmente. */
                      <span className="num">{dias === 0 ? 'hoje' : `há ${dias}d`}</span>
                    )}
                  </td>
                  <td className="fila__acao">
                    {/* "Laudar" só para quem lauda. O solicitante abre a mesma
                        tela, mas para acompanhar o que pediu -- oferecer a ele
                        um botão de laudar é convidá-lo a uma ação que o
                        servidor recusa. */}
                    <Botao
                      tamanho="sm"
                      variante={assinados || solicitados ? 'neutro' : 'primario'}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        navegar(`/laudos/${e.id}`);
                      }}
                    >
                      {assinados || solicitados ? 'Ver' : 'Laudar'}
                    </Botao>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Lista>

      {solicitados ? (
        <p className="campo__dica" style={{ marginTop: 'var(--e-3)' }}>
          Aparecem os exames que você solicitou. Quando o laudo ficar pronto, abra o exame para
          baixar o PDF.
        </p>
      ) : (
        !assinados && (
          <p className="campo__dica" style={{ marginTop: 'var(--e-3)' }}>
            Aparecem os exames com radiografia no sistema que estão com você ou ainda sem médico
            definido. <Selo estagio="AGUARDANDO_LAUDO" />
          </p>
        )
      )}
    </Layout>
  );
}
